import { useState, useMemo } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Search,
  FileSearch,
  AlertTriangle,
  ArrowRight,
  Loader2,
  FolderOpen,
  Building2,
  GitBranch,
  CheckCircle2,
} from "lucide-react";
import type {
  ActionItemsListResponse,
  ActionItemRecord,
  ReferenceListsResponse,
  ReferenceListItemRecord,
} from "@shared/v2/types";

const CLOSED_STATUSES = ["CLOSED", "SENT_UPWARD", "CANCELLED"];

function isOverdue(dueDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + "T00:00:00");
  return due < today;
}

function statusLabel(status: string): string {
  switch (status) {
    case "REGISTERED": return "Registered";
    case "IN_PROGRESS": return "In Progress";
    case "WITH_SDF_MD_REVIEW": return "SDF MD Review";
    case "WITH_EDGE_MD_REVIEW": return "EDGE MD Review";
    case "WITH_SECRETARIAT_TO_SEND": return "With Secretariat";
    case "SENT_UPWARD": return "Sent Upward";
    case "AWAITING_RESPONSE": return "Awaiting Response";
    case "SIGNED_OFF": return "Signed Off";
    case "CLOSED": return "Closed";
    case "CANCELLED": return "Cancelled";
    default: return status;
  }
}

function priorityColor(priority: string): string {
  switch (priority) {
    case "P1": return "text-red-400";
    case "P2": return "text-orange-400";
    case "P3": return "text-blue-400";
    case "P4": return "text-white/50";
    default: return "text-white/50";
  }
}

interface OriginGroup {
  meetingType: string;
  originReference: string;
  meetingTypeLabel: string;
  items: ActionItemRecord[];
  openCount: number;
  overdueCount: number;
}

type GroupMode = "origin" | "company" | "completed";

const LANE_LABELS: Record<string, string> = {
  EDGE_CHAIRMAN: "Edge Chairman",
  EDGE_MD_OFFICE: "Edge MD Office",
  BOARD_EXCOM: "Board ExCom",
  SDF_MD: "SDF MD",
};

export default function OriginTracePage() {
  const { containerClass } = useLayout();
  const [filterType, setFilterType] = useState<string>("__all__");
  const [searchText, setSearchText] = useState("");
  const [groupMode, setGroupMode] = useState<GroupMode>("origin");
  const [completedFilterLane, setCompletedFilterLane] = useState<string>("__all__");
  const [completedSearchText, setCompletedSearchText] = useState("");

  const itemsQuery = useQuery<ActionItemsListResponse>({
    queryKey: ["/api/action-items"],
  });

  const refListsQuery = useQuery<ReferenceListsResponse>({
    queryKey: ["/api/governance/reference-lists"],
  });

  const meetingTypeLabels = useMemo(() => {
    const refLists = refListsQuery.data?.data ?? {};
    const meetingTypes: ReferenceListItemRecord[] = refLists["MEETING_TYPE"] ?? [];
    const map: Record<string, string> = {};
    for (const item of meetingTypes) {
      map[item.item_key] = item.label;
    }
    return map;
  }, [refListsQuery.data]);

  const allItems = useMemo(() => itemsQuery.data?.data ?? [], [itemsQuery.data]);

  const originGroups = useMemo<OriginGroup[]>(() => {
    const groupMap = new Map<string, OriginGroup>();

    for (const item of allItems) {
      const mt = item.meeting_type || item.origin_type || "(unknown)";
      const ref = item.origin_reference || "(no reference)";
      const key = `${mt}::${ref}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          meetingType: mt,
          originReference: ref,
          meetingTypeLabel: meetingTypeLabels[mt] || mt.replace(/_/g, " "),
          items: [],
          openCount: 0,
          overdueCount: 0,
        });
      }

      const group = groupMap.get(key)!;
      group.items.push(item);

      if (!CLOSED_STATUSES.includes(item.status)) {
        group.openCount++;
        if (item.due_date && isOverdue(item.due_date)) {
          group.overdueCount++;
        }
      }
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.meetingType !== b.meetingType) return a.meetingType.localeCompare(b.meetingType);
      return a.originReference.localeCompare(b.originReference);
    });
  }, [allItems, meetingTypeLabels]);

  const companyGroups = useMemo<OriginGroup[]>(() => {
    const groupMap = new Map<string, OriginGroup>();

    for (const item of allItems) {
      const companyName = item.meeting_name?.trim() || "(no company)";
      const key = companyName.toLowerCase();

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          meetingType: "",
          originReference: companyName,
          meetingTypeLabel: "",
          items: [],
          openCount: 0,
          overdueCount: 0,
        });
      }

      const group = groupMap.get(key)!;
      group.items.push(item);

      if (!CLOSED_STATUSES.includes(item.status)) {
        group.openCount++;
        if (item.due_date && isOverdue(item.due_date)) {
          group.overdueCount++;
        }
      }
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.originReference === "(no company)") return 1;
      if (b.originReference === "(no company)") return -1;
      return a.originReference.localeCompare(b.originReference);
    });
  }, [allItems]);

  const activeGroups = groupMode === "origin" ? originGroups : companyGroups;

  const distinctMeetingTypes = useMemo(() => {
    const types = new Set(originGroups.map((g) => g.meetingType).filter(Boolean));
    return Array.from(types).sort();
  }, [originGroups]);

  const filteredGroups = useMemo(() => {
    let groups = activeGroups;

    if (groupMode === "origin" && filterType !== "__all__") {
      groups = groups.filter((g) => g.meetingType === filterType);
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      groups = groups.filter(
        (g) =>
          g.originReference.toLowerCase().includes(lower) ||
          g.meetingTypeLabel.toLowerCase().includes(lower) ||
          g.items.some((i) =>
            i.title.toLowerCase().includes(lower) ||
            (i.meeting_name && i.meeting_name.toLowerCase().includes(lower))
          )
      );
    }

    return groups;
  }, [activeGroups, filterType, searchText, groupMode]);

  const completedItems = useMemo(() => {
    let items = allItems.filter((i) => CLOSED_STATUSES.includes(i.status));

    if (completedFilterLane !== "__all__") {
      items = items.filter((i) => i.authority_lane === completedFilterLane);
    }

    if (completedSearchText.trim()) {
      const lower = completedSearchText.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(lower) ||
          (i.meeting_name && i.meeting_name.toLowerCase().includes(lower)) ||
          (i.origin_reference && i.origin_reference.toLowerCase().includes(lower)) ||
          (i.assigned_to && i.assigned_to.toLowerCase().includes(lower))
      );
    }

    return items;
  }, [allItems, completedFilterLane, completedSearchText]);

  const distinctCompletedLanes = useMemo(() => {
    const lanes = new Set(allItems.filter((i) => CLOSED_STATUSES.includes(i.status)).map((i) => i.authority_lane));
    return Array.from(lanes).sort();
  }, [allItems]);

  const isLoading = itemsQuery.isLoading || refListsQuery.isLoading;

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#FF9F43]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
    >
      <div className={`${containerClass} mx-auto px-6 py-8`}>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileSearch className="h-6 w-6 text-[#FF9F43]" />
            <h1 className="text-2xl font-bold text-white" data-testid="text-page-title">
              Origin Trace
            </h1>
          </div>
          <p className="text-white/50 text-sm">
            View action items by where they came from — meetings, opportunities, or other sources.
          </p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Button
            variant={groupMode === "origin" ? "default" : "outline"}
            size="sm"
            onClick={() => { setGroupMode("origin"); setFilterType("__all__"); }}
            className="gap-1.5"
            data-testid="button-group-by-origin"
          >
            <GitBranch className="h-3.5 w-3.5" />
            By Meeting
          </Button>
          <Button
            variant={groupMode === "company" ? "default" : "outline"}
            size="sm"
            onClick={() => { setGroupMode("company"); setFilterType("__all__"); }}
            className="gap-1.5"
            data-testid="button-group-by-company"
          >
            <Building2 className="h-3.5 w-3.5" />
            By Company
          </Button>
          <Button
            variant={groupMode === "completed" ? "default" : "outline"}
            size="sm"
            onClick={() => { setGroupMode("completed"); }}
            className="gap-1.5"
            data-testid="button-group-completed"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Completed
          </Button>
        </div>

        {groupMode === "completed" ? (
          <>
            <div className="flex items-end gap-4 mb-6 flex-wrap">
              <div className="flex flex-col gap-1.5">
                <Label className="text-white/60 text-sm">Authority Lane</Label>
                <Select value={completedFilterLane} onValueChange={setCompletedFilterLane}>
                  <SelectTrigger
                    className="w-[220px] bg-white/5 border-white/10 text-white"
                    data-testid="select-filter-completed-lane"
                  >
                    <SelectValue placeholder="All lanes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All lanes</SelectItem>
                    {distinctCompletedLanes.map((l) => (
                      <SelectItem key={l} value={l}>
                        {LANE_LABELS[l] || l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
                <Label className="text-white/60 text-sm">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input
                    value={completedSearchText}
                    onChange={(e) => setCompletedSearchText(e.target.value)}
                    placeholder="Search by title, company, reference, or assigned person..."
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-search-completed"
                  />
                </div>
              </div>
            </div>

            <div className="mb-4 text-white/50 text-sm">
              {completedItems.length} completed item{completedItems.length !== 1 ? "s" : ""}
            </div>

            {completedItems.length === 0 ? (
              <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
                <CardContent className="p-8 flex flex-col items-center gap-3">
                  <CheckCircle2 className="h-10 w-10 text-white/30" />
                  <p className="text-white/50" data-testid="text-empty-completed">
                    {completedSearchText || completedFilterLane !== "__all__"
                      ? "No matching completed items found."
                      : "No completed items yet."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-1">
                {completedItems.map((item) => (
                  <Link
                    key={item.id}
                    href={`/action-items/${item.id}`}
                    className="block"
                    data-testid={`link-completed-item-${item.id}`}
                  >
                    <Card
                      className="border-white/8 transition-colors hover-elevate"
                      style={{ background: "rgba(40, 199, 111, 0.04)", borderColor: "rgba(40, 199, 111, 0.15)" }}
                    >
                      <CardContent className="p-3 flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <CheckCircle2 className="h-4 w-4 text-[#28C76F] shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-white truncate" data-testid={`text-completed-title-${item.id}`}>
                                {item.title}
                              </span>
                              <Badge variant="outline" className={`text-xs ${priorityColor(item.priority)} border-current`}>
                                {item.priority}
                              </Badge>
                              <Badge variant="outline" className={`text-xs ${
                                item.status === "CANCELLED"
                                  ? "text-red-400 border-red-400/30"
                                  : item.status === "SENT_UPWARD"
                                    ? "text-[#00CFE8] border-[#00CFE8]/30"
                                    : "text-[#28C76F] border-[#28C76F]/30"
                              }`}>
                                {statusLabel(item.status)}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40 flex-wrap">
                              <span>{LANE_LABELS[item.authority_lane] || item.authority_lane}</span>
                              <span className="text-white/20">|</span>
                              <span>{item.assigned_to}</span>
                              {item.meeting_name && (
                                <>
                                  <span className="text-white/20">|</span>
                                  <span>{item.meeting_name}</span>
                                </>
                              )}
                              {item.origin_reference && (
                                <>
                                  <span className="text-white/20">|</span>
                                  <span>{item.origin_reference}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/30 shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-end gap-4 mb-6 flex-wrap">
              {groupMode === "origin" && (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-white/60 text-sm">Meeting Type</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger
                      className="w-[220px] bg-white/5 border-white/10 text-white"
                      data-testid="select-filter-meeting-type"
                    >
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All types</SelectItem>
                      {distinctMeetingTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {meetingTypeLabels[t] || t.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
                <Label className="text-white/60 text-sm">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                  <Input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={groupMode === "origin" ? "Search by origin reference or item title..." : "Search by company name or item title..."}
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-search-origin"
                  />
                </div>
              </div>
            </div>

            {filteredGroups.length === 0 && (
              <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
                <CardContent className="p-8 flex flex-col items-center gap-3">
                  <FolderOpen className="h-10 w-10 text-white/30" />
                  <p className="text-white/50" data-testid="text-empty-state">
                    {searchText || filterType !== "__all__"
                      ? "No matching action items found."
                      : "No action items with origin data yet."}
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="space-y-6">
              {filteredGroups.map((group) => (
                <div key={groupMode === "origin" ? `${group.meetingType}::${group.originReference}` : group.originReference} data-testid={`origin-group-${groupMode === "origin" ? `${group.meetingType}-${group.originReference}` : group.originReference}`}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                {groupMode === "origin" ? (
                  <Badge
                    variant="outline"
                    className="text-[#FF9F43] border-[#FF9F43]/40 text-xs"
                  >
                    {group.meetingTypeLabel}
                  </Badge>
                ) : (
                  <Building2 className="h-4 w-4 text-[#00CFE8]" />
                )}
                <h2 className="text-lg font-semibold text-white">
                  {groupMode === "origin" ? (
                    group.originReference === "(no reference)" ? (
                      <span className="text-white/40 italic">No reference provided</span>
                    ) : (
                      group.originReference
                    )
                  ) : (
                    group.originReference === "(no company)" ? (
                      <span className="text-white/40 italic">No company specified</span>
                    ) : (
                      group.originReference
                    )
                  )}
                </h2>
                <div className="flex items-center gap-3 ml-auto flex-wrap">
                  <span className="text-white/50 text-sm">{group.items.length} item{group.items.length !== 1 ? "s" : ""}</span>
                  {group.openCount > 0 && (
                    <Badge variant="outline" className="text-cyan-300 border-cyan-800/50 text-xs">
                      {group.openCount} open
                    </Badge>
                  )}
                  {group.overdueCount > 0 && (
                    <Badge variant="outline" className="text-red-400 border-red-800/50 text-xs">
                      {group.overdueCount} overdue
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-1 ml-2">
                {group.items.map((item) => {
                  const overdue =
                    !CLOSED_STATUSES.includes(item.status) &&
                    item.due_date &&
                    isOverdue(item.due_date);
                  return (
                    <Link
                      key={item.id}
                      href={`/action-items/${item.id}`}
                      className="block"
                      data-testid={`link-origin-item-${item.id}`}
                    >
                      <Card
                        className="border-white/8 transition-colors hover-elevate"
                        style={{
                          background: overdue
                            ? "rgba(255,50,50,0.06)"
                            : "rgba(255,255,255,0.04)",
                          borderColor: overdue
                            ? "rgba(255,50,50,0.2)"
                            : undefined,
                        }}
                      >
                        <CardContent className="p-3 flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className="text-sm font-medium text-white truncate"
                                  data-testid={`text-origin-item-title-${item.id}`}
                                >
                                  {item.title}
                                </span>
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${priorityColor(item.priority)} border-current`}
                                >
                                  {item.priority}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 text-xs text-white/40 flex-wrap">
                                <span>{item.assigned_to}</span>
                                <span className="text-white/20">|</span>
                                <span>{item.authority_lane}</span>
                                {item.due_date && (
                                  <span className={overdue ? "text-red-400" : ""}>
                                    {overdue && (
                                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                                    )}
                                    Due {new Date(item.due_date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs border-white/15 text-white/60"
                              data-testid={`text-origin-item-status-${item.id}`}
                            >
                              {statusLabel(item.status)}
                            </Badge>
                            <ArrowRight className="h-4 w-4 text-white/30" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
